import { useForm, FormProvider, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import classNames from "classnames";
import { Heading, Text } from "../../Type";
import ToggleSelection from "../../ToggleSelection";
import TextInput from "../../TextInput";
import Button from "../../Button";
import RadioGroup from "../../RadioGroup";
import TextLink from "../../TextLinks";
import ErrorMessage from "../../ErrorMessage";
import { UserRoleTypeType } from "../../../types/userType";
import {
  Check,
  ChevronBottom,
  X,
  Search,
} from "../../Icons/general-interface-1";

import {
  clearIndicator,
  dropdownIndicator,
  indicatorSeparator,
  menuList,
  valueContainer,
} from "../../FilterDropdowns/commonStyles";
import styles from "./index.module.scss";
import commonStyles from "../../FilterDropdowns/index.module.scss";

export type NewUserDataType = {
  role: string;
  email: string;
  first_name: string;
  last_name: string;
  cep?: string;
  location?: string;
};

type PropsType = {
  locations: Array<{ id: number; name: string }>;
  roles: Array<{ id: string; name: string; type: string }>;
  onCancel: () => void;
  onSubmit: (data: NewUserDataType) => void;
  submitting?: boolean;
  defaultOption?: UserRoleTypeType;
};

const UserForm = ({
  onCancel,
  onSubmit,
  locations,
  roles,
  submitting,
  defaultOption,
}: PropsType) => {
  const repRole = roles.find((r) => r.type === "sales_rep");
  const authRole = roles.find((r) => r.type === "authorizer");
  const methods = useForm<NewUserDataType>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      role: (defaultOption === "sales_rep" ? repRole?.id : authRole?.id) || "",
      email: "",
      first_name: "",
      last_name: "",
      cep: "",
      location: "",
    },
  });

  const watchRole = methods.watch("role");

  const Option = (props) => (
    <components.Option {...props}>
      <div className={styles.optionLabel}>{props.data.label}</div>
      {props.isSelected && <Check className={styles.iconCheck} />}
    </components.Option>
  );

  const DropdownIndicator = ({ ...props }) => (
    <components.DropdownIndicator {...props}>
      <ChevronBottom />
    </components.DropdownIndicator>
  );

  const ClearIndicator = ({ ...props }) => (
    <components.ClearIndicator {...props}>
      <X />
    </components.ClearIndicator>
  );

  const Placeholder = (props) => {
    return (
      <components.Placeholder {...props}>
        <Search className={styles.placeholderIcon} />
        {props.children}
      </components.Placeholder>
    );
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      display: "inline-flex",
      background: "white",
      minWidth: "100%",
      padding: "0 10px",
      border: state.isFocused
        ? `1px solid ${commonStyles.grey_400}`
        : `1px solid ${commonStyles.grey_200}`,
      boxShadow: state.isFocused
        ? `0px 0px 0px 3px ${commonStyles.blue_200}`
        : `0px 0px 0px 3px transparent`,
      borderRadius: "4px",
      "&:focus, &:hover": {
        borderColor: commonStyles.grey_400,
      },
    }),
    clearIndicator,
    dropdownIndicator,
    indicatorSeparator,
    menu: (provided) => ({
      ...provided,
      width: "100%",
      borderRadius: "4px",
      boxShadow: "0px 1px 3px 0px #3F3F4426, 0px 0px 0px 1px #3F3F440D",
      zIndex: "1000",
    }),
    menuList,
    placeholder: (provided) => ({
      ...provided,
      fontSize: "16px",
      color: commonStyles.grey_300,
      display: "flex",
      alignItems: "center",
    }),
    valueContainer,
    option: (provided, state) => ({
      ...provided,
      width: "100%",
      padding: "12px 16px",
      background: state.isFocused ? commonStyles.blue_100 : "transparent",
      display: "flex",
      alignItems: "flex-start",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: commonStyles.grey_100,
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      transition: "color 150ms ease-in-out",
      ":hover": {
        backgroundColor: commonStyles.grey_100,
        color: commonStyles.red_300,
      },
    }),
  };

  return (
    <FormProvider {...methods}>
      <form
        className={styles.container}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Heading size="h4">User permissions</Heading>
        <Text>
          This selection cannot be modified after the user creates their
          account.
        </Text>
        <ToggleSelection
          name="role"
          options={roles.map((r) => ({
            label: r.name,
            value: r.id,
            id: r.type,
            color:
              r.type === "sales_rep" ? ("blue" as const) : ("green" as const),
          }))}
        />
        <Heading size="h4">User information</Heading>
        <Text>A signup link will be sent to the email entered below.</Text>
        <div className={styles.grid}>
          <TextInput
            id="email"
            name="email"
            label="Email"
            requiredText="This is a required field."
            className={classNames(styles.g1, styles.requiredText)}
          />
          <TextInput
            id="first_name"
            name="first_name"
            label="First Name"
            requiredText="This is a required field."
            className={classNames(styles.g2, styles.requiredText)}
          />
          <TextInput
            id="last_name"
            name="last_name"
            label="Last Name"
            requiredText="This is a required field."
            className={classNames(styles.g3, styles.requiredText)}
          />
          {watchRole === repRole?.id && (
            <>
              <div className={styles.select}>
                <Text
                  size="large"
                  className={classNames(
                    styles.selectLabel,
                    styles.requiredText
                  )}
                >
                  Primary Motion Location
                </Text>
                <Controller
                  control={methods.control}
                  name="location"
                  rules={{ required: "This is a required field." }}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <>
                      <Select
                        styles={customStyles}
                        classNamePrefix="locations"
                        placeholder="Choose location"
                        onChange={onChange}
                        options={locations.map((l) => ({
                          value: l.id,
                          label: l.name,
                        }))}
                        maxMenuHeight="197px"
                        isSearchable
                        isClearable
                        components={{
                          ClearIndicator,
                          DropdownIndicator,
                          Option,
                          Placeholder,
                        }}
                      />
                      {error && (
                        <ErrorMessage field="location">
                          {error.message || "This is a required field."}
                        </ErrorMessage>
                      )}
                    </>
                  )}
                />
              </div>
              <RadioGroup
                name="cep"
                label="Is this user CEP enabled?"
                className={classNames(styles.g1, styles.requiredText)}
                options={[
                  {
                    label: "Yes",
                    value: "true",
                  },
                  { label: "No", value: "false" },
                ]}
                horizontal
                requiredText="This is a required field."
              />
            </>
          )}
        </div>
        <div className={styles.buttonRow}>
          <TextLink onClick={onCancel}>Cancel</TextLink>
          <Button primary submit disabled={submitting}>
            {submitting ? "Loading..." : "Create new user"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default UserForm;
